import { Grid, Skeleton, Stack, TableCell, Typography } from "@mui/material";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient } from "react-query";
import { generateColorGradient, handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import {
  activeWorkshopsPerMonth,
  mandayPerMonthAndYear,
  mandayPerVenue,
  topNSubjects,
  workshopsAttendance,
  workshopsPerProvider,
  workshopsStatusCount,
} from "framework/charts";
import { IChartView, IFormField, IOption } from "models/app";
import { CHARTS, InputTypes } from "constants/enums";
import ChartView from "components/UI/ChartView/ChartView";
import { WorkshopChartsCells } from "framework/workshops";
import { IWorkshop } from "models/workshop";
import Filters from "./partials/filters";
import StackedBarChart from "./partials/stacked";
import { ChartData } from "chart.js";
import FormFields from "components/UI/FormFields";
import { useForm } from "react-hook-form";
import { getPermission, getUserData } from "helpers";
import { IPermission } from "models/permissions";
import EmptyContent from "components/EmptyContent";
import BlockIcon from "@mui/icons-material/Block";
interface Props {
  feature: Feature;
}

const Analytics: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loading3, setLoading3] = useState<boolean>(true);
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [loadingYear, setLoadingYear] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const [yearStr, setYear] = useState<string[]>([
    new Date().getFullYear().toString(),
  ]);
  const [data1, setData1] = useState<any[]>([]);
  const [data2, setData2] = useState<any[]>([]);
  const [data3, setData3] = useState<any[]>([]);
  const [data4, setData4] = useState<any[]>([]);
  const [data5, setData5] = useState<any[]>([]);
  const [data6, setData6] = useState<any[]>([]);
  const [data7, setData7] = useState<any[]>([]);
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const [dateFrom, setDateFrom] = useState<any>(new Date(year, month, 1));
  const [dateTo, setDateTo] = useState<any>(new Date(year, month + 1, 0));
  const user = getUserData();
  const { colors: colors1 } = user;
  const colors: string[] = generateColorGradient(colors1?.chartMainColor);

  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let promise1 = queryClient.fetchQuery("get Workshops per provider", () =>
        workshopsPerProvider(
          `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
        )
      );

      let promise2 = queryClient.fetchQuery("get Workshops status count", () =>
        workshopsStatusCount(
          `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
        )
      );

      let promise3 = queryClient.fetchQuery("get Workshops Attendance", () =>
        workshopsAttendance(
          `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
        )
      );

      let promise5 = queryClient.fetchQuery(
        "get manday per month and year",
        () => mandayPerMonthAndYear(`?years=${yearStr}&fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`)
      );

      let promise4 = queryClient.fetchQuery(
        "get Active Workshops Per Month",
        () =>
          activeWorkshopsPerMonth(
            `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
          )
      );

      let promise6 = queryClient.fetchQuery("get total manday per veneu", () =>
        mandayPerVenue(
          `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
        )
      );

      let promise7 = queryClient.fetchQuery("get top 10 subjects", () =>
        topNSubjects(
          `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}&num=10`
        )
      );

      const [
        response1,
        response2,
        response3,
        response4,
        response5,
        response6,
        response7,
      ] = await Promise.all([
        promise1,
        promise2,
        promise3,
        promise4,
        promise5,

        promise6,
        promise7,
      ]);
      setData1(response1);
      setData2(response2);
      setData3(response3);
      setData4(response4);
      setData5(response5);
      setData6(response6);
      setData7(response7);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    setLoading3(true);
    try {
      const res = await getPermission(21);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchYear = useCallback(async () => {
    setLoadingYear(true);
    try {
      let promise5 = queryClient.fetchQuery(
        "get manday per month and year",
        () => mandayPerMonthAndYear(`?years=${yearStr}`)
      );

      const [response5] = await Promise.all([promise5]);
      setData5(response5);
      setLoadingYear(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoadingYear(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert, yearStr]);

  useEffect(() => {
    fetchData();
    fetchPermission();

    // eslint-disable-next-line
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchYear();

    // eslint-disable-next-line
  }, [yearStr]);

  useEffect(() => {
    fetchPermission();

    // eslint-disable-next-line
  }, []);
  const getBodyCells = (row: IWorkshop) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "vendorName")
        return {
          id: "vendorName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.vendorName : row.vendorNameAr}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "venueName")
        return {
          id: "venueName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.venueName : row.venueNameAr}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "topicName")
        return {
          id: "topicName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.topicName : row.topicNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "roomNumber")
        return {
          id: "roomNumber",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.roomNumber}</Typography>
            </TableCell>
          ),
        };
      if (key === "capacity")
        return {
          id: "capacity",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.capacity}</Typography>
            </TableCell>
          ),
        };

      if (key === "fromDate")
        return {
          id: "fromDate",
          element: (
            <TableCell align={"left"} key={i}>
              {row.fromDate && (
                <Typography variant="body2">
                  {new Date(row.fromDate).toLocaleDateString()}
                </Typography>
              )}
            </TableCell>
          ),
        };
      if (key === "toDate")
        return {
          id: "toDate",
          element: (
            <TableCell align={"left"} key={i}>
              {row.toDate && (
                <Typography variant="body2">
                  {new Date(row.toDate).toLocaleDateString()}
                </Typography>
              )}
            </TableCell>
          ),
        };
      else return <></>;
    });

    return cells;
  };

  const {
    control,
    watch,
    setValue,
    register,
    formState: { errors },
  } = useForm<any>({ mode: "onChange" });

  function getMonthLabel(month: number): string {
    switch (month) {
      case 1:
        return "Jan";
      case 2:
        return "Feb";
      case 3:
        return "Mar";
      case 4:
        return "Apr";
      case 5:
        return "May";
      case 6:
        return "Jun";
      case 7:
        return "Jul";
      case 8:
        return "Aug";
      case 9:
        return "Sept";
      case 10:
        return "Oct";
      case 11:
        return "Nov";
      case 12:
        return "Dec";

      default:
        return "";
    }
  }

  const chartData1: IChartView = {
    data: {
      Labels: data1.map((item: any) => item?.vendorName),
      Values: data1.map((item: any) => item?.workshopsCount),
    },
    label: t("T_dashboard_3"),
    type: CHARTS.BAR,
    backgroundColor: colors1?.chartMainColor || "#513cc7",

    borderColor: "#c3aaeb",

    Id: "workshopsPerProviders",
  };

  const chartData7: IChartView = {
    data: {
      Labels: data7.map((item: any) => item?.name),
      Values: data7.map((item: any) => item?.topicCount),
    },
    label: t("T_dashboard_5"),
    type: CHARTS.BAR,
    backgroundColor: colors1?.chartMainColor || "#513cc7",

    borderColor: "#c3aaeb",

    Id: "top10subjects",
  };

  const chartData2: IChartView = {
    data: {
      Labels: data2.map((item: any) => item?.status),
      Values: data2.map((item: any) => item?.statusCount),
    },
    label: t("T_dashboard_2"),
    type: CHARTS.PIE,
    backgroundColorPie: generateColorGradient(
      colors1?.chartMainColor,
      data2.length
    ),
    borderColorPie: colors,
    Id: "workshopsStatusCount",
  };

  const chartData6: IChartView = {
    data: {
      Labels: data6.map((item: any) => item?.venueName),
      Values: data6.map((item: any) => item?.totalDays),
    },
    label: t("T_dashboard_4"),
    type: CHARTS.BAR,
    backgroundColor: colors1?.chartMainColor,
    borderColor: "#c3aaeb",
    Id: "mandayPerVenue",
  };

  const data11: ChartData<"bar"> = {
    labels: data3.map((item: any) => item.departmentName),
    datasets: [
      {
        label: t("T_dashboard_9"),
        data: data3.map((item: any) => item.totalCount),
        backgroundColor: generateColorGradient(
          colors1?.chartMainColor,
          2,
          1,
          1
        )[0],
      },
      {
        label: t("T_dashboard_8"),
        data: data3.map((item: any) => item.attendeesCount),
        backgroundColor: generateColorGradient(
          colors1?.chartMainColor,
          2,
          1,
          1
        )[1],
      },
    ],
  };

  let currentYear = new Date().getFullYear();
  let lastFiveYears = Array.from(
    { length: 5 },
    (v, k) => currentYear - k
  ).reverse();
  let next50Years = Array.from({ length: 50 }, (v, k) => k + currentYear);
  // remove duplicates
  let years = [...lastFiveYears, ...next50Years].filter(
    (value, index, self) => self.indexOf(value) === index
  );

  let yearsOptions: IOption[] = years.map((year: number) => {
    return { label: year.toString(), value: year.toString() };
  });

  let filterClientsFields: IFormField[] = [
    {
      name: "Year",
      label: "Years",
      yearsOnly: true,
      type: InputTypes.MULTI_SELECT,
      multiple: true,
      defaultValue: [
        {
          label: new Date().getFullYear().toString(),
          value: new Date().getFullYear().toString(),
        },
      ],
      options: yearsOptions,
      onChange: (value: any) => {
        setYear(value.map((item: IOption) => item.value));
      },
    },
  ];

  const generateDatasets = (data: any) => {
    const uniqueYears = [...new Set(data.map((item: any) => item.year))];

    return uniqueYears.map((year: any, index) => {
      const color = generateColorGradient(
        colors1?.chartMainColor,
        uniqueYears.length,
        2,
        2
      )[index % uniqueYears.length];
      return {
        label: year.toString(),
        data: data
          .filter((item: any) => item.year === year)
          .map((item: any) => item.days),
        borderColor: color,
        backgroundColor: color,
        pointBackgroundColor: color,
        pointBorderColor: "#fff",
      };
    });
  };

  const chartData: IChartView = {
    data: {
      Labels: [],
      Values: [],
    },
    chartData: {
      labels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item: any) =>
        getMonthLabel(item)
      ),
      datasets: generateDatasets(data5),
    },
    label: t("T_dashboard_6"),
    type: CHARTS.LINE2,
    Id: "AnnualMandaysByMonth",
  };

  if (loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  if (!permission?.hasReadPermission)
    return (
      <EmptyContent
        icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
        header={t("T_no_permissions")}
        message={t("T_no_permissions_message")}
      />
    );
  return (
    <Stack spacing={2} p={2}>
      {loading ? (
        <Stack spacing={2} p={2}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton variant="rectangular" key={i} width="100%" height={30} />
          ))}
        </Stack>
      ) : (
        <div>
          <Typography
            variant="h4"
            sx={{
              alignItems: "center",
            }}
          >
            {t("T_dashboard_1")}
          </Typography>
          <List
            headCells={WorkshopChartsCells}
            data={data4}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        </div>
      )}

      <Grid
        container
        justifyContent={"space-between"}
        alignItems="center"
        sx={{ mb: 4 }}
      >
        {/* <Typography variant="h3" color={"header.primary"}>
            {t(feature.name)}
          </Typography> */}
        <Stack direction={"row"} spacing={1}>
          <Filters
            onChangeFrom={(e) => setDateFrom(e)}
            onChangeTo={(e) => setDateTo(e)}
            valueFrom={dateFrom}
            valueTo={dateTo}
          />
        </Stack>
      </Grid>
      {loading ? (
        <Stack spacing={2} p={2}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton variant="rectangular" key={i} width="100%" height={30} />
          ))}
        </Stack>
      ) : (
      
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ChartView {...chartData2} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ChartView {...chartData1} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ChartView {...chartData6} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <ChartView {...chartData7} />
            </Grid>

            <Grid item xs={12} sm={6}>
              {filterClientsFields.map((field: any, index: number) => (
                <Grid item xs={12} key={index}>
                  <FormFields
                    {...field}
                    control={control}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    register={register}
                  />
                </Grid>
              ))}
              {loadingYear ? (
                <Stack spacing={2} p={2}>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <Skeleton
                      variant="rectangular"
                      key={i}
                      width="100%"
                      height={15}
                    />
                  ))}
                </Stack>
              ) : (
                <ChartView {...chartData} />
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
            
              <div>
                <Typography
                  variant="h4"
                  sx={{
                    alignItems: "center",
                  }}
                >
                  {t("T_dashboard_7")}
                </Typography>
                <StackedBarChart data={data11} />
              </div>
            </Grid>
          </Grid>
      
      )}
    </Stack>
  );
};

export default Analytics;
